/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { BookOpen, Bot, Building2, Layers, Shield, UserCircle, Users } from 'lucide-react';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import { getScopeLevel, hasAnyRoleKey } from '../utils/scope';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const SETTINGS_SECTIONS = [
  {
    type: 'item',
    to: '/profile',
    icon: UserCircle,
    label: 'Profile',
    matchPaths: ['/profile', '/settings'],
  },
  {
    type: 'section',
    title: 'ADMINISTRATION',
    items: [
      {
        to: '/users',
        icon: Users,
        label: 'Users',
        permission: 'users.view',
        matchPaths: ['/users'],
        excludeRoleKeys: ['px_account_admin', 'px_manager'],
      },
      {
        to: '/roles',
        icon: Shield,
        label: 'Roles',
        permission: 'roles.view',
        matchPaths: ['/roles'],
        excludeRoleKeys: ['px_account_admin'],
      },
      {
        to: '/accounts',
        icon: Building2,
        label: ACCOUNT_LABEL.plural,
        permission: 'accounts.view',
        matchPaths: ['/accounts'],
        excludePaths: ['/sub-accounts'],
        excludeScopes: ['account', 'sub_account'],
      },
      {
        to: '/sub-accounts',
        icon: Layers,
        label: SUB_ACCOUNT_LABEL.plural,
        permission: 'sub_accounts.view',
        matchPaths: ['/sub-accounts'],
        excludeScopes: ['sub_account'],
        // Hidden for these roles even when they hold sub_accounts.view
        excludeRoleKeys: ['px_manager'],
      },
    ],
  },
  {
    type: 'section',
    title: 'SMART TROUBLESHOOTING',
    items: [
      {
        to: '/knowledge-bases',
        icon: BookOpen,
        label: 'Knowledge Bases',
        permission: 'knowledge_bases.view',
        matchPaths: ['/knowledge-bases'],
      },
      {
        to: '/ai-agents',
        icon: Bot,
        label: 'AI Agents',
        permission: 'ai_agents.view',
        matchPaths: ['/ai-agents'],
      },
    ],
  },
];

export const isSettingsRoute = (pathname) => {
  const settingsPrefixes = ['/profile', '/appearance', '/settings', '/users', '/roles', '/accounts', '/sub-accounts', '/knowledge-bases', '/ai-agents'];
  return settingsPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const SettingsPanel = ({ onNavigateMobile }) => {
  const { can } = usePermissions();
  const { user } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  const isItemActive = (item) => {
    if (item.excludePaths?.some((p) => currentPath.startsWith(p))) {
      return false;
    }
    return item.matchPaths?.some((p) => currentPath === p || currentPath.startsWith(`${p}/`));
  };

  const isItemVisible = useCallback((item) => {
    if (item.excludeScopes?.includes(getScopeLevel(user))) return false;
    if (hasAnyRoleKey(user, item.excludeRoleKeys)) return false;
    if (!item.permission) return true;
    const parts = item.permission.split('.');
    if (parts.length === 2) {
      const [resource, action] = parts;
      return can[resource] && can[resource][action] && can[resource][action]();
    }
    return true;
  }, [user, can]);

  const visibleMobileItems = useMemo(() => {
    const items = [];
    SETTINGS_SECTIONS.forEach((sec) => {
      if (sec.type === 'item') {
        if (isItemVisible(sec)) items.push(sec);
      } else if (sec.type === 'section') {
        sec.items.forEach((item) => {
          if (isItemVisible(item)) items.push(item);
        });
      }
    });
    return items;
  }, [isItemVisible]);

  return (
    <>
      {/* Mobile Horizontal Subnav */}
      <nav
        aria-label="Settings Submenu Mobile"
        className="lg:hidden w-full shrink-0 flex items-center gap-1.5 overflow-x-auto no-scrollbar px-3 py-2.5 bg-base-200/90 backdrop-blur-md border-b border-base-content/10 shadow-xs"
      >
        {visibleMobileItems.map((item) => {
          const active = isItemActive(item);
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigateMobile}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-150 shrink-0 ${
                active
                  ? 'bg-primary text-primary-content shadow-sm'
                  : 'text-base-content/70 hover:text-primary hover:bg-base-content/5'
              }`}
            >
              <Icon size={14} className="shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Desktop Vertical Panel */}
      <aside
        aria-label="Settings Navigation"
        className="hidden lg:flex lg:w-64 xl:w-72 shrink-0 bg-base-200 lg:rounded-xl border border-base-content/5 p-4 lg:p-5 flex-col overflow-y-auto no-scrollbar shadow-sm animate-fade-in"
      >
        {/* Header */}
        <div className="mb-4 pb-3 border-b border-base-content/10">
          <h2 className="text-xl font-black text-base-content tracking-tight">
            Settings
          </h2>
          <p className="text-xs text-base-content/50 font-semibold mt-0.5 leading-snug">
            Manage your profile and administration
          </p>
        </div>

        {/* Nav List */}
        <nav className="flex-1 space-y-1.5" aria-label="Settings Submenu">
          {SETTINGS_SECTIONS.map((section, idx) => {
            if (section.type === 'item') {
              if (!isItemVisible(section)) return null;
              const active = isItemActive(section);
              const Icon = section.icon;

              return (
                <NavLink
                  key={section.to}
                  to={section.to}
                  onClick={onNavigateMobile}
                  className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 group ${
                    active
                      ? 'bg-primary/10 text-primary border border-primary/20 shadow-sm font-bold'
                      : 'text-base-content/70 hover:text-primary hover:bg-base-content/5'
                  }`}
                >
                  <Icon
                    size={19}
                    className={`shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                      active ? 'text-primary' : 'text-base-content/60 group-hover:text-primary'
                    }`}
                  />
                  <span className="truncate">{section.label}</span>
                </NavLink>
              );
            }

            if (section.type === 'section') {
              const visibleItems = section.items.filter(isItemVisible);
              if (visibleItems.length === 0) return null;

              return (
                <div key={section.title || idx} className="pt-4">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-base-content/40 px-3 pb-2">
                    {section.title}
                  </div>
                  <div className="space-y-1">
                    {visibleItems.map((item) => {
                      const active = isItemActive(item);
                      const Icon = item.icon;

                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={onNavigateMobile}
                          className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 group ${
                            active
                              ? 'bg-primary/10 text-primary border border-primary/20 shadow-sm font-bold'
                              : 'text-base-content/70 hover:text-primary hover:bg-base-content/5'
                          }`}
                        >
                          <Icon
                            size={19}
                            className={`shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                              active ? 'text-primary' : 'text-base-content/60 group-hover:text-primary'
                            }`}
                          />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return null;
          })}
        </nav>
      </aside>
    </>
  );
};

SettingsPanel.propTypes = {
  onNavigateMobile: PropTypes.func,
};

export default SettingsPanel;
